import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getDefaultSubscriptionSelector,
  getPetSelector,
  getSubscriptionCycleSelector,
} from '@modules/subscriptions/core/selectors';
import { getCurrentOrderSelector } from '@modules/order-schedule/core/selectors';
import { WelcomeMessage } from './components/welcome-message/WelcomeMessage';
import { UpcomingOrders } from './components/upcoming-orders/UpcomingOrders';
import { RecipeOverview } from './components/recipe-overview/RecipeOverview';
import { Supplements } from './components/supplements/Supplements';
import { CustomerTag } from '@modules/dashboard/components/customer-tag/CustomerTag';
import { getCustomerSelector } from '@modules/customer';
import { SubscriptionReactivation } from './components/subscription-reactivation/SubscriptionReactivation';
import { SubscriptionList } from './components/subscription-list/SubscriptionList';
import { RafLink } from './components/raf-link/RafLink';
import { Headline } from '@components/headline/Headline';
import { Section } from '@layout/components';
import { SubscriptionTimeline } from '@modules/subscription-timeline/SubscriptionTimeline';
import { getMaxRewardOrder } from '@modules/subscription-timeline/utils/rewardsConfig';
import { StoreCreditPfotenpunkteCard } from '@modules/store-credit/components/StoreCreditPfotenpunkteCard';
import { NoRewardBanner } from './components/reward-banner/NoRewardBanner';
import { GrowthPhaseReminderModal } from '@modules/subscriptions/components/growth-phase/GrowthPhaseReminderModal';
import { PetProfileUpdateModal } from '@modules/subscriptions/components/pet-profile-update';
import { IPet } from '@modules/subscriptions/core/types';
import { PuppyToAdultTransition } from '@modules/subscriptions/components/pet-transition/PuppyToAdultTransition';
import { FormattedMessage } from 'react-intl';

export const DashboardPage = () => {
  const order = useSelector(getCurrentOrderSelector);
  const customer = useSelector(getCustomerSelector);
  const defaultSubscription = useSelector(getDefaultSubscriptionSelector);
  const cycle = useSelector(getSubscriptionCycleSelector);
  const pet = useSelector(getPetSelector);
  const isActive = defaultSubscription?.status === 'ACTIVE';
  const [isPetUpdateModalOpen, setIsPetUpdateModalOpen] = useState(false);

  const currentFoc = useMemo(() => {
    const tags = Array.isArray(customer.tags)
      ? customer.tags
      : typeof customer.tags === 'string'
        ? (customer.tags as string).split(',')
        : [];

    const focTag = tags.find((t: string) => t.trim().startsWith('FOC-'));
    if (!focTag) return null;

    const match = focTag.trim().match(/FOC-(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }, [customer]);

  // Each delivery cycle has its own gift schedule ending on a different delivery
  // (6 on a 56-day plan, 36 on a 7-day one), so the cut-off has to come from the
  // schedule rather than a hardcoded pair of numbers.
  const maxRewardOrder = getMaxRewardOrder(cycle);
  const shouldShowTimeline =
    defaultSubscription?.status !== 'CANCELLED' &&
    currentFoc !== null &&
    currentFoc <= maxRewardOrder;

  return (
    <>
      {isActive && (
        <div className="lg:hidden">
          <Section>
            <Headline hasDropdown>
              <FormattedMessage id="MENU.DASHBOARD" />
            </Headline>
          </Section>
        </div>
      )}

      <PuppyToAdultTransition />

      <CustomerTag />

      <div className="dashboard-page w-full md:col-span-2 md:grid md:grid-cols-12 lg:grid-cols-2 gap-[1.2rem]items-center">
        <div className="md:col-span-5 lg:col-span-1">
          <h1 className="dashboard-page-title">
            {/* The first name is optional, so the comma before it belongs to the
                message rather than being concatenated on — some languages punctuate
                a direct address differently. */}
            <FormattedMessage
              id="DASHBOARD.WELCOME.HEADING"
              values={{
                hasName: customer?.firstName ? 'yes' : 'no',
                name: customer?.firstName ?? '',
                highlight: (chunks) => <span>{chunks}</span>,
              }}
            />
          </h1>
        </div>
        {isActive && (
          <div className="hidden md:block md:col-span-7 lg:col-span-1">
            <RafLink />
          </div>
        )}
      </div>

      {!isActive && (
        <div className="grid grid-cols-1 mb-[1.2rem] pt-[3rem]">
          <SubscriptionReactivation />
        </div>
      )}

      <div className="dashboard-page-wrapper grid grid-cols-1 md:grid-cols-2 mb-[1.5rem] gap-[1.2rem]">
        {isActive && (
          <div className="w-full md:col-span-2 md:grid md:grid-cols-12 lg:grid-cols-2 gap-[1.2rem]">
            <div className="md:col-span-5 lg:col-span-1">
              <WelcomeMessage />
            </div>

            {shouldShowTimeline && (
              <div className="relative md:col-span-7 lg:col-span-1 subscription-timeline-wrapper-el">
                <SubscriptionTimeline />
              </div>
            )}

            {(currentFoc === null || currentFoc > maxRewardOrder) && (
              <div className="relative md:col-span-7 lg:col-span-1 md:h-full">
                <NoRewardBanner />
              </div>
            )}
          </div>
        )}
        {isActive && (
          <>
            <div className="dashboard-page-recipes md:col-span-1">
              <RecipeOverview />
            </div>
            <div className="dashboard-page-orders md:col-span-1">
              <UpcomingOrders order={order} />
            </div>
          </>
        )}
        <div className="w-full dashboard-page-store-credit md:col-span-2 lg:hidden">
          <StoreCreditPfotenpunkteCard />
        </div>
        {/* Subscription List and Supplements */}
        <div className="dashboard-page-subscription-list overflow-hidden md:overflow-visible px-[2px] pb-[2px] md:pb-0 md:px-0 w-full md:col-span-2 md:grid md:grid-cols-12 lg:grid-cols-2 gap-[1.2rem] flex flex-col h-full">
          <div className="flex-grow h-full md:col-span-6 lg:col-span-1">
            <SubscriptionList />
          </div>
          <div className="dashboard-page-supplements md:col-span-6 lg:col-span-1">
            <Supplements />
          </div>
        </div>
      </div>

      {pet && (
        <>
          <GrowthPhaseReminderModal
            pet={pet as IPet}
            onUpdateClick={() => setIsPetUpdateModalOpen(true)}
          />
          <PetProfileUpdateModal
            isOpen={isPetUpdateModalOpen}
            onClose={() => setIsPetUpdateModalOpen(false)}
          />
        </>
      )}
    </>
  );
};
